import { Zap, Link2 } from "lucide-react";
import { useLang } from "@/context/AppContext";
import { REACTION_ICONS } from "@/components/BroadcastLive";

const fmtT = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export const computeHighlights = (reactions, duration, max = 5) => {
  if (!reactions.length) return [];
  const win = Math.max(5, Math.round(duration / 40));
  const buckets = {};
  reactions.forEach((r) => {
    const k = Math.floor(r.t / win);
    const b = (buckets[k] ||= { start: k * win, count: 0, emojis: {} });
    b.count += 1;
    b.emojis[r.emoji] = (b.emojis[r.emoji] || 0) + 1;
  });
  const picked = [];
  Object.values(buckets).filter((b) => b.count >= 2).sort((a, b) => b.count - a.count).forEach((b) => {
    if (picked.length < max && !picked.some((p) => Math.abs(p.start - b.start) <= win)) picked.push(b);
  });
  return picked.map((b) => ({ ...b, end: Math.min(duration, b.start + win), top: Object.entries(b.emojis).sort((x, y) => y[1] - x[1])[0][0] }))
    .sort((a, b) => a.start - b.start);
};

export const HighlightMarkers = ({ highlights, duration, onJump }) => (
  <div className="pointer-events-none absolute inset-x-0 -top-3 h-3" data-testid="highlight-markers">
    {highlights.map((h, i) => (
      <button key={h.start} onClick={() => onJump(h)} title={`${fmtT(h.start)} · ${h.count}`} data-testid={`highlight-marker-${i}`}
        className="group pointer-events-auto absolute -top-2 grid h-6 w-5 -translate-x-1/2 place-items-center"
        style={{ left: `${(h.start / duration) * 100}%` }}>
        <span className="h-3 w-1.5 rounded-sm bg-yellow-300 shadow-[0_0_8px_rgba(234,179,8,0.8)] transition-transform group-hover:scale-150" />
      </button>
    ))}
  </div>
);

export const HighlightList = ({ highlights, pos, onJump, onShare }) => {
  const { t } = useLang();
  if (!highlights.length) return <p className="mt-3 text-xs text-slate-500" data-testid="highlights-empty">{t("no_highlights")}</p>;
  const max = Math.max(...highlights.map((h) => h.count));
  return (
    <div className="mt-3" data-testid="highlights-list">
      <p className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-yellow-300"><Zap className="h-3.5 w-3.5" />{t("highlights")}</p>
      <div className="flex flex-wrap gap-2">
        {highlights.map((h, i) => {
          const [I, c] = REACTION_ICONS[h.top];
          const current = pos >= h.start && pos < h.end;
          return (
            <span key={h.start} className="inline-flex items-center gap-1">
            <button onClick={() => onJump(h)} data-testid={`highlight-chip-${i}`}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs transition-all duration-200 hover:-translate-y-0.5 ${current ? "border-yellow-300 bg-yellow-300/15 text-yellow-200" : "border-yellow-400/30 text-slate-200 hover:border-yellow-300"}`}>
              <span className="font-mono">{fmtT(h.start)}</span>
              <I className={`h-3.5 w-3.5 ${c}`} />
              <span className="font-mono text-slate-400">{h.count}</span>
              {h.count === max && <span className="font-mono text-[9px] uppercase tracking-widest text-yellow-300">{t("peak")}</span>}
            </button>
            <button onClick={() => onShare(h)} title={t("share_moment")} data-testid={`highlight-share-${i}`}
              className="grid h-6 w-6 place-items-center rounded-full text-slate-500 transition-colors hover:bg-cyan-400/10 hover:text-cyan-300"><Link2 className="h-3.5 w-3.5" /></button>
            </span>
          );
        })}
      </div>
    </div>
  );
};
